import { familiaLabelFromRaw } from './productosMeseroReportModel';
import { applyEncabezadoFiltrosUiFromFallback } from './reporteFiltrosEncabezadoExport';

const num = (v) => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

function keyNorm(key) {
  return String(key)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function pickByKeyPatterns(obj, patterns) {
  const keys = Object.keys(obj);
  for (const re of patterns) {
    const k = keys.find((kk) => re.test(kk) || re.test(keyNorm(kk)));
    if (k != null) return obj[k];
  }
  return undefined;
}

/** Normaliza una fila del API de popularidad al layout tipo PDF (8 columnas estándar). */
export function normalizePopularidadRow(raw) {
  if (!raw || typeof raw !== 'object') return null;

  const familia = familiaLabelFromRaw(raw);
  const codigo = String(
    pickByKeyPatterns(raw, [/^codigo$/i, /^clave$/i, /^sku$/i]) ?? '-'
  ).trim() || '-';

  const nombreVal =
    pickByKeyPatterns(raw, [
      /^nombre$/i,
      /^nombreproducto$/i,
      /^nombre_producto$/i,
      /^descripcion$/i,
      /^producto$/i,
    ]) ??
    pickByKeyPatterns(raw, [/descrip/i, /^articulo$/i, /^descripcionproducto$/i]);
  const nombre = nombreVal != null && nombreVal !== '' ? String(nombreVal) : '—';

  let cantidad = num(pickByKeyPatterns(raw, [/^cantidad$/i, /^cant\.?$/i, /^qty$/i, /^cant\.vendida$/i]));
  if (cantidad == null) cantidad = 0;

  let costo = num(
    pickByKeyPatterns(raw, [/^costo$/i, /^costounitario$/i, /^costo_unitario$/i])
  );
  let tCosto = num(
    pickByKeyPatterns(raw, [/^tcosto$/i, /^tcost$/i, /^totalcosto$/i, /^costototal$/i, /^t\.costo$/i])
  );
  let precio = num(
    pickByKeyPatterns(raw, [/^precio$/i, /^preciounitario$/i, /^precio_unitario$/i])
  );
  let importe = num(
    pickByKeyPatterns(raw, [/^importe$/i, /^total$/i, /^venta$/i, /^subtotal$/i, /^importeventa$/i])
  );
  let utilidad = num(pickByKeyPatterns(raw, [/^utilidad$/i, /^util$/i]));
  let impuestoBA = num(pickByKeyPatterns(raw, [/^impuestoba$/i, /^impuesto_ba$/i, /^impuestobebidasalcoholicas$/i]));

  if (tCosto == null && costo != null && cantidad != null) tCosto = costo * cantidad;
  if (importe == null && precio != null && cantidad != null) importe = precio * cantidad;
  if (utilidad == null && importe != null && tCosto != null) utilidad = importe - tCosto;

  return {
    familia,
    codigo,
    nombre,
    cantidad,
    costo,
    tCosto,
    precio,
    importe,
    utilidad,
    impuestoBA,
  };
}

export function sumGroupTotals(rows) {
  const t = { cantidad: 0, tCosto: 0, importe: 0, utilidad: 0, impuestoBA: 0 };
  for (const r of rows) {
    t.cantidad += Number(r.cantidad) || 0;
    t.tCosto += Number(r.tCosto) || 0;
    t.importe += Number(r.importe) || 0;
    t.utilidad += Number(r.utilidad) || 0;
    t.impuestoBA += Number(r.impuestoBA) || 0;
  }
  return t;
}

/**
 * Agrupa filas normalizadas conservando el orden de aparición de cada familia
 * (alineado con tabla ordenada).
 * @param {ReturnType<typeof normalizePopularidadRow>[]} normRows
 * @returns {[string, ReturnType<typeof normalizePopularidadRow>[]][]}
 */
export function groupPopularidadInDisplayOrder(normRows) {
  const list = normRows.filter(Boolean);
  const map = new Map();
  const order = [];
  for (const r of list) {
    const k = r.familia || 'Sin categoría';
    if (!map.has(k)) {
      map.set(k, []);
      order.push(k);
    }
    map.get(k).push(r);
  }
  return order.map((k) => [k, map.get(k)]);
}

export const POPULARIDAD_COLUMN_LABELS = [
  'Código',
  'Nombre',
  'Cantidad',
  'Costo',
  'TCosto',
  'Precio',
  'Importe',
  'Utilidad',
];

function meaningfulString(v) {
  return v != null && String(v).trim() !== '';
}

function foldKeyLoose(k) {
  return String(k)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s-]/g, '')
    .toLowerCase();
}

const ENC_DOM_FOLD_KEYS = new Set([
  'domiciliofiscal',
  'domicilio',
  'direccion',
  'direccionfiscal',
  'direccioncompleta',
  'direccionsucursal',
  'domiciliocompleto',
  'domiciliosucursal',
  'domicilioempresa',
  'ubicacion',
  'address',
]);

/** RFC / domicilio con nombres distintos en sesión o API (p. ej. Dirección, RFC_Empresa). */
const ENC_RFC_FOLD_KEYS = new Set([
  'rfc',
  'rfcempresa',
  'rfcfiscal',
  'rfccontribuyente',
  'registrofederalcontribuyentes',
]);

export function pickEncabezadoDomicilioLoose(obj) {
  if (!obj || typeof obj !== 'object') return '';
  for (const [k, v] of Object.entries(obj)) {
    if (ENC_DOM_FOLD_KEYS.has(foldKeyLoose(k)) && meaningfulString(v)) return String(v).trim();
  }
  return '';
}

export function pickEncabezadoRfcLoose(obj) {
  if (!obj || typeof obj !== 'object') return '';
  for (const [k, v] of Object.entries(obj)) {
    if (ENC_RFC_FOLD_KEYS.has(foldKeyLoose(k)) && meaningfulString(v)) return String(v).trim();
  }
  return '';
}

function hasOwnMeaningfulScalar(obj, k) {
  if (!obj || typeof obj !== 'object' || !(k in obj)) return false;
  const v = obj[k];
  if (v == null) return false;
  if (typeof v === 'number') return !Number.isNaN(v);
  if (typeof v === 'string') return v.trim() !== '';
  if (typeof v === 'boolean') return true;
  return true;
}

/**
 * API popularidad (y similares): encabezado con `empresa`, `reporte` y opcional `sucursal`.
 * Los trae al mismo plano que usa la vista/PDF (`nombreComercial`, `reporteTitulo`, etc.).
 * @param {Record<string, unknown>} raw
 * @returns {Record<string, unknown>}
 */
export function flattenNestedEncabezadoApiShape(raw) {
  if (!raw || typeof raw !== 'object') return {};
  const o = { ...raw };

  if (!meaningfulString(o.reporteTitulo) && meaningfulString(o.titulo)) {
    o.reporteTitulo = String(o.titulo).trim();
  }
  if (!meaningfulString(o.nombreComercial) && meaningfulString(o.empresaNombre)) {
    o.nombreComercial = String(o.empresaNombre).trim();
  }

  const em = o.empresa;
  if (em && typeof em === 'object') {
    if (!meaningfulString(o.nombreComercial) && meaningfulString(em.nombre)) {
      o.nombreComercial = String(em.nombre).trim();
    }
    if (!meaningfulString(o.razonSocial) && meaningfulString(em.razonSocial)) {
      o.razonSocial = String(em.razonSocial).trim();
    }
    if (!meaningfulString(o.rfc) && meaningfulString(em.rfc)) {
      o.rfc = String(em.rfc).trim();
    }
    if (!meaningfulString(o.domicilioFiscal) && meaningfulString(em.domicilioFiscal)) {
      o.domicilioFiscal = String(em.domicilioFiscal).trim();
    }
    const dLoose = pickEncabezadoDomicilioLoose(em);
    if (!meaningfulString(o.domicilioFiscal) && dLoose) o.domicilioFiscal = dLoose;
    const rLoose = pickEncabezadoRfcLoose(em);
    if (!meaningfulString(o.rfc) && rLoose) o.rfc = rLoose;
  }

  const rep = o.reporte;
  if (rep && typeof rep === 'object') {
    if (!meaningfulString(o.reporteTitulo) && meaningfulString(rep.titulo)) {
      o.reporteTitulo = String(rep.titulo).trim();
    }
    for (const [k, v] of Object.entries(rep)) {
      if (k === 'titulo') continue;
      if (v == null) continue;
      if (typeof v === 'string' && !v.trim()) continue;
      if (hasOwnMeaningfulScalar(o, k)) continue;
      o[k] = v;
    }
  }

  const suc = o.sucursal;
  if (suc && typeof suc === 'object') {
    if (!meaningfulString(o.nombreComercial)) {
      for (const kk of ['nombre', 'nombreSucursal', 'nombreComercial']) {
        if (meaningfulString(suc[kk])) {
          o.nombreComercial = String(suc[kk]).trim();
          break;
        }
      }
    }
    if (!meaningfulString(o.razonSocial) && meaningfulString(suc.razonSocial)) {
      o.razonSocial = String(suc.razonSocial).trim();
    }
    if (!meaningfulString(o.rfc) && meaningfulString(suc.rfc)) o.rfc = String(suc.rfc).trim();
    if (!meaningfulString(o.domicilioFiscal)) {
      const dS = pickEncabezadoDomicilioLoose(suc);
      if (dS) o.domicilioFiscal = dS;
    }
  }

  delete o.empresa;
  delete o.reporte;
  delete o.sucursal;

  return o;
}

/**
 * Copia alias de encabezado del API a campos canónicos cuando faltan.
 * @param {Record<string, unknown>} apiEnc
 * @returns {Record<string, unknown>}
 */
export function normalizePopularidadApiEncabezado(apiEnc) {
  if (!apiEnc || typeof apiEnc !== 'object') return {};
  const o = { ...apiEnc };
  if (!meaningfulString(o.nombreComercial)) {
    for (const k of [
      'empresaNombre',
      'nombreSucursal',
      'sucursalNombre',
      'nombreTienda',
      'sucursal',
      'tienda',
    ]) {
      const v = o[k];
      if (meaningfulString(v)) {
        o.nombreComercial = String(v).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.responsable)) {
    for (const k of [
      'nombreResponsable',
      'representante',
      'representanteLegal',
      'nombreCompletoUsuario',
      'nombreUsuario',
      'nombreEmpleado',
      'empleadoNombre',
    ]) {
      const v = o[k];
      if (meaningfulString(v)) {
        o.responsable = String(v).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.razonSocial)) {
    for (const k of ['razon_social', 'razonSocialEmpresa', 'nombreEmpresa']) {
      const v = o[k];
      if (meaningfulString(v)) {
        o.razonSocial = String(v).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.domicilioFiscal)) {
    for (const k of ['domicilio', 'direccion', 'direccionFiscal', 'domicilioCompleto', 'domicilio_fiscal']) {
      const v = o[k];
      if (meaningfulString(v)) {
        o.domicilioFiscal = String(v).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.rfc)) {
    for (const k of Object.keys(o)) {
      if (String(k).toLowerCase() === 'rfc' && meaningfulString(o[k])) {
        o.rfc = String(o[k]).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.rfc)) {
    for (const k of ['rfcEmpresa', 'rfcFiscal', 'rfcContribuyente']) {
      const v = o[k];
      if (meaningfulString(v)) {
        o.rfc = String(v).trim();
        break;
      }
    }
  }
  if (!meaningfulString(o.domicilioFiscal)) {
    const d = pickEncabezadoDomicilioLoose(o);
    if (d) o.domicilioFiscal = d;
  }
  if (!meaningfulString(o.rfc)) {
    const r = pickEncabezadoRfcLoose(o);
    if (r) o.rfc = r;
  }
  return o;
}

/**
 * `encabezado` en raíz del JSON o anidado en `data` (reportes popularidad / mesero).
 * @param {object|null} json respuesta completa
 * @param {unknown} data `json.data`
 * @returns {object|null}
 */
export function extractEncabezadoFromPopularidadPayload(json, data) {
  const candidates = [
    json?.encabezado,
    json?.header?.encabezado,
    data && typeof data === 'object' && !Array.isArray(data) ? data.encabezado : null,
    data && typeof data === 'object' && !Array.isArray(data) ? data.header?.encabezado : null,
    data && typeof data === 'object' && !Array.isArray(data) ? data.meta?.encabezado : null,
  ];
  for (const e of candidates) {
    if (e && typeof e === 'object' && Object.keys(e).length > 0) return e;
  }
  return null;
}

/** API puede mandar `''` en campos; no deben borrar el fallback. */
export function mergeEncabezadoPreferBaseForEmpty(base, patch) {
  const out = { ...base };
  if (!patch || typeof patch !== 'object') return out;
  for (const [k, v] of Object.entries(patch)) {
    if (v == null) continue;
    if (typeof v === 'string' && v.trim() === '') continue;
    out[k] = v;
  }
  return out;
}

/** Lee totales de bebidas alcohólicas si el API los envía junto al payload (objeto o raíz). */
/** Une `json.encabezado` con valores por defecto y datos de sesión (sin pisar lo que mande el API). */
export function mergePopularidadEncabezado(apiEnc, encabezadoFallback, opts = {}) {
  const {
    reporteTitulo = 'REPORTE POPULARIDAD DE PRODUCTOS',
    incluirCortesias = false,
    lineaCortesias = 'INCLUYENDO CORTESIAS',
    cortesiasSubtitle,
  } = opts;
  const userFb =
    encabezadoFallback && typeof encabezadoFallback === 'object' ? encabezadoFallback : {};
  const apiFlat = flattenNestedEncabezadoApiShape(
    apiEnc && typeof apiEnc === 'object' ? apiEnc : {}
  );
  const bloqueSoloEnc = Boolean(
    apiFlat.bloqueTexto &&
      meaningfulString(apiFlat.bloqueTexto) &&
      !meaningfulString(apiFlat.reporteTitulo) &&
      !meaningfulString(apiFlat.nombreComercial)
  );
  const base = bloqueSoloEnc
    ? {}
    : {
        ...userFb,
        reporteTitulo,
        ...(incluirCortesias ? { cortesias: cortesiasSubtitle || lineaCortesias } : {}),
      };
  const merged = mergeEncabezadoPreferBaseForEmpty(base, apiFlat);
  return normalizePopularidadApiEncabezado(
    applyEncabezadoFiltrosUiFromFallback(merged, userFb),
  );
}

export function extractPopularidadFooterFromPayload(obj) {
  if (!obj || typeof obj !== 'object') return {};
  const n = (v) => {
    if (v == null || v === '') return null;
    const x = Number(v);
    return Number.isFinite(x) ? x : null;
  };
  const readFromAny = (source, keys = []) => {
    if (!source || typeof source !== 'object') return null;
    for (const k of keys) {
      const val = n(source?.[k]);
      if (val != null) return val;
    }
    return null;
  };
  const readFromRows = (source, keys = []) => {
    if (!source || typeof source !== 'object') return null;
    const rows = Array.isArray(source) ? source : Array.isArray(source?.data) ? source.data : [];
    for (const row of rows) {
      const val = readFromAny(row, keys);
      if (val != null) return val;
    }
    return null;
  };
  const out = {};
  const subtotalKeys = [
    'subtotalBebidasAlcoholicasSinIva',
    'subtotal_bebidas_alcoholicas_sin_iva',
    'subtotal',
    'subTotal',
    'subtotalGeneral',
    'totalSubtotal',
  ];
  const netoKeys = [
    'netoBebidasAlcoholicas',
    'neto_bebidas_alcoholicas',
    'netoBebidas',
    'netoBebidasAlcoholicos',
    'subtotalBebidasAlcoholicasNeto',
    'subtotal_bebidas_alcoholicas_neto',
  ];
  const importeKeys = [
    'importeBebidasAlcoholicas',
    'importe_bebidas_alcoholicas',
    'importeBebidasAlcoholicos',
    'subtotalBebidasAlcoholicasSinIva',
    'subtotal_bebidas_alcoholicas_sin_iva',
  ];
  const subtotal =
    readFromAny(obj, subtotalKeys) ??
    readFromAny(obj?.totales, subtotalKeys) ??
    readFromRows(obj, subtotalKeys);
  const neto =
    readFromAny(obj, netoKeys) ??
    readFromAny(obj?.totales, netoKeys) ??
    readFromRows(obj, netoKeys);
  const imp =
    readFromAny(obj, importeKeys) ??
    readFromAny(obj?.totales, importeKeys) ??
    readFromRows(obj, importeKeys);
  if (subtotal != null) out.subtotal = subtotal;
  if (neto != null) out.netoBebidasAlcoholicas = neto;
  if (imp != null) out.importeBebidasAlcoholicas = imp;
  return out;
}
